import React, { useState, useEffect } from "react";
import axios from 'axios';
import { Box, Typography, Grid, CircularProgress, Divider, Card } from '@mui/material';
import { Formik, Form } from 'formik';
import { GET_INVOICE_EXPORT_PROFORMA_INVOICE, READ_COMMERCIAL_INVOICE_EXPORT } from '../../../services/apiRoutes';
import CustomCardView from '../../common/CustomCardView';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import DetailsParticulars from "../../common/DetailsPage/DetailsParticulars";
import CustomPrintModel from "../../common/CustomPrint";
import { convertNumberToWords, printFunction } from "../../../services/apiServices";
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { CommercialInvoiceContentFirst, CommercialInvoiceContentFour, CommercialInvoiceContentSecond, CommercialInvoiceContentThird, } from '../PrintPdf/CommercialInvoicePrint';
import { genericPost } from "../../../services/authServices";
import { getRoundAmount } from "../../../services/CommonServices";
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import { t } from "../../../constants/translationHelper";

const CommercialInvoiceView = ({ handleClose, shipmentOrgId, commercialInvExpId, companyName, phoneNumber, expCommercialPrefix, expCommercialSuffix, expProformaPrefix, expProformaSuffix, templateId }) => {
    const companyDateFormat = useCompanyDateFormat();

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataParticular, setDataParticular] = useState(null);
    const [totalQty, setTotalQty] = useState(0);
    const [totalRate, setTotalRate] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [amountData, setAmount] = useState(0);
    const [amountText, setAmountText] = useState(0);
    const [currencyName, setCurrencyName] = useState('');

    // Fetch data for the commercial invoice

    const [termConditions, setTermConditions] = useState([]);
    const [companyBanks, setCompanyBanks] = useState([]);
    const [shipments, setShipments] = useState([]);
    const [shippingBooking, setShippingBooking] = useState([]);
    const [paymentVoucher, setPaymentVoucher] = useState([]);
    const [companyTaxes, setCompanyTaxes] = useState([]);
    const [shippingmark, setShippingmark] = useState([]);

    const number = amountText || '';
    const amountInWords = convertNumberToWords(number);

    useEffect(() => {
        fetchDataInvoice();
    }, []);

    const fetchDataInvoice = async () => {
        setLoading(true);
        try {
            const payload = { shipmentOrgId: shipmentOrgId };
            const response = await genericPost(GET_INVOICE_EXPORT_PROFORMA_INVOICE, payload);
            if (response?.data?.success) {
                const resData = response.data.data;
                setTermConditions(resData.termConditions || []);
                setCompanyBanks(resData.companyBanks || []);
                setShipments(resData.shipments || []);
                setShippingBooking(resData.shippingBooking || []);
                setPaymentVoucher(resData.paymentVoucher || []);
                setCompanyTaxes(resData.companyTaxes || []);
                setShippingmark(resData.shippingmark || []);
            } else {
                setError('No data found');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Error fetching data');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (!commercialInvExpId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_COMMERCIAL_INVOICE_EXPORT(commercialInvExpId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                const fetchedData = response.data.data;
                const symbol = fetchedData?.currency?.currency_symbol || '';
                const symbolName = fetchedData?.currency?.currency_name || '';
                const totalQty = fetchedData.particulars
                    .reduce((total, particular) => total + (parseFloat(particular.qty) || 0), 0)

                const totalRate = fetchedData.particulars.reduce((sum, item) => sum + parseFloat(item.rate), 0);
                setData(fetchedData);
                const particulars = fetchedData.particulars.map(particular => ({
                    qty: particular.qty || 0,
                    rate: `${symbol || ''} ${parseFloat(particular?.rate || 0).toFixed(2)}`,
                    amounts: `${symbol || ''} ${parseFloat(particular?.amount || 0).toFixed(2)}`,
                    description: particular.description || "",
                    amount: (particular.qty || 0) * (parseFloat(particular.rate) || 0),
                    unitName: particular.productsUnit.unitName,
                    // productName: particular.products.productName,
                    productName: (
                        <div style={{ display: 'block', }}>
                            <div>
                                {particular.products.productName || '-'}
                            </div>

                            {particular.products.iupac && (
                                <div style={{ fontSize: '10px' }}>
                                    <strong>IUPAC :</strong> {particular.products.iupac}
                                </div>
                            )}

                            {particular.products.casNo && (
                                <div style={{ fontSize: '10px' }}>
                                    <strong>CAS No :</strong> {particular.products.casNo}
                                </div>
                            )}
                        </div>
                    ),



                }));
                const amountTotal = particulars.reduce((acc, item) => acc + item.amount, 0);
                const totalAmount = `${symbol} ${amountTotal}`;
                const roundedAmount = getRoundAmount(amountTotal);
                setAmount(`${symbol} ${roundedAmount.toFixed(2)}`);
                setAmountText(roundedAmount);
                setDataParticular(particulars);
                setTotalQty(totalQty);
                setTotalRate(totalRate);
                setTotalAmount(totalAmount);
                setCurrencyName(symbolName);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [commercialInvExpId]);

    // Show loading spinner or error message
    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const formatPort = (port, country) => [port, country].filter(Boolean).join(", ");
    // Form values
    const values = {
        userId: data?.userId || '',
        companyId: data?.companyId || '',
        customerNamesId: companyName || '',
        customer: data?.customer?.clientCompanyName || '',
        bookingNumber: data?.booking?.bookingNumber || '',
        phoneNumber: phoneNumber || '',
        reference: data?.reference || '',
        commercialInvoiceNo: `${expCommercialPrefix || ''} ${data?.commercialInvoiceNo || ''} ${expCommercialSuffix || ''}`,
        date: companyDateFormat(data?.date) || '',
        dueDate: companyDateFormat(data?.dueDate) || '',
        note: data?.note || '',
        currency: data?.currency?.currency || '',
        proformaInvoiceNo: `${expProformaPrefix || ''} ${data?.ProformaInvExp?.proformaInvoiceNo || ''} ${expProformaSuffix || ''}`,
        portOfLoading: formatPort(
            data?.loading?.portName,
            data?.loading?.country?.name
        ),
        portOfDestination: formatPort(
            data?.destination?.portName,
            data?.destination?.country?.name
        ),
        manufacturerName: data?.manufacturerName || '',
    };

    // Labels for displaying in CustomCardView expProformaPrefix, expProformaSuffix
    const labels = [
        { key: 'customer', label: 'Customer', sm: 12, md: 4, },
        { key: 'dueDate', label: 'Due Date', sm: 12, md: 4, },
        { key: 'commercialInvoiceNo', label: 'Commercial Invoice', sm: 12, md: 4, },
        { key: 'bookingNumber', label: 'Booking Number', sm: 12, md: 4, },
        { key: 'currency', label: 'currency', sm: 12, md: 4, },
        { key: 'date', label: 'Date', sm: 12, md: 4, },
        { key: 'proformaInvoiceNo', label: 'proforma Invoice', sm: 12, md: 4, },
        { key: 'reference', label: 'Reference', sm: 12, md: 4, },
        { key: 'portOfLoading', label: 'port Of Loading', sm: 12, md: 4, },
        { key: 'portOfDestination', label: 'port Of Destination', sm: 12, md: 4, },
        { key: 'manufacturerName', label: 'Manufacturer Name', sm: 12, md: 4, },
    ];

    const particularsColumns = [
        {
            id: 'productName',
            label: 'Product Name',
            accessor: 'productName',
        },

        {
            id: 'qty',
            label: 'Quantity',
            accessor: 'qty',
            styleAccessor: {
                textAlign: 'center',
            },
        },
        {
            id: 'rate',
            label: 'Rate',
            accessor: 'rate',
            styleAccessor: {
                textAlign: 'right',
            },
        },
        {
            id: 'unitName',
            label: 'Unit',
            accessor: 'unitName',
            styleAccessor: {
                textAlign: 'center',
            },
        },
        {
            id: 'amounts',
            label: 'Amount',
            accessor: 'amounts',
            styleAccessor: {
                textAlign: 'right',
            },
        },
    ];
    const footerData = [
        {
            label: t('Total'),
            footerValue: '',
            style: {
                textAlign: 'right',
            },
            colSpan: 1,

        },
        {
            label: '',
            footerValue: totalQty,
            style: {
                textAlign: 'center',

            },

        },
        {
            label: '',
            footerValue: amountData,
            style: {
                textAlign: 'right',

            },
            colSpan: 3,

        },
    ];

    const clientCompanyName = data?.customer?.clientCompanyName || '';
    const encodedCompanyName = clientCompanyName.replace(/ /g, '').toLowerCase();
    const fileName = `commercial_invoice_${encodedCompanyName}.pdf`;

    const printContent = () => {
        const elementId = 'contentId';
        const iframeId = 'print-iframe';
        printFunction(elementId, iframeId, fileName);
    };
    const pdfContent = () => {
        const elementId = 'contentpdf';
        const iframeId = 'print-iframe';
        printFunction(elementId, iframeId, fileName);
    };

    const invoiceTermConditions = termConditions?.find(
        (item) => item.voucherName === "4"
    )?.termConditions || "";

    // const check = parseInt(templateId);
    const footerFalse = false;
    const footerTrue = true;

    const check = parseInt(templateId);

    const getPrintContent = () => {
        if (check === 1) {
            return (<CommercialInvoiceContentFirst
                commercialInvoiceNo={values.commercialInvoiceNo}
                totalQty={totalQty}
                amountData={amountData}
                invoiceData={data}
                termConditions={invoiceTermConditions}
                companyBanks={companyBanks}
                shippingBooking={shippingBooking}
                shipments={shipments}
                paymentVoucher={paymentVoucher}
                companyTaxes={companyTaxes}
                amountInWords={amountInWords}
                footer={footerFalse} />);
        }
        if (check === 2) {
            return (
                <CommercialInvoiceContentSecond commercialInvoiceNo={values.commercialInvoiceNo}
                    totalQty={totalQty}
                    amountData={amountData}
                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    amountInWords={amountInWords}
                    footer={footerFalse} />);
        }
        if (check === 3) {
            return (
                <CommercialInvoiceContentThird commercialInvoiceNo={values.commercialInvoiceNo}
                    totalQty={totalQty}
                    amountData={amountData}
                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    amountInWords={amountInWords}
                    footer={footerFalse} />);
        }
        if (check === 4) {
            return (
                <CommercialInvoiceContentFour commercialInvoiceNo={values.commercialInvoiceNo}
                    totalQty={totalQty}
                    amountData={amountData}
                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    amountInWords={amountInWords}
                    footer={footerFalse} />);
        }

        return <CommercialInvoiceContentFirst commercialInvoiceNo={values.commercialInvoiceNo}
            totalQty={totalQty}
            amountData={amountData}
            invoiceData={data}
            termConditions={invoiceTermConditions}
            companyBanks={companyBanks}
            shippingBooking={shippingBooking}
            shipments={shipments}
            paymentVoucher={paymentVoucher}
            companyTaxes={companyTaxes}
            amountInWords={amountInWords}
            footer={footerFalse} />;
    };

    const getPdfContent = () => {
        if (check === 1) {
            return (<CommercialInvoiceContentFirst
                commercialInvoiceNo={values.commercialInvoiceNo}
                totalQty={totalQty}
                amountData={amountData}
                invoiceData={data}
                termConditions={invoiceTermConditions}
                companyBanks={companyBanks}
                shippingBooking={shippingBooking}
                shipments={shipments}
                paymentVoucher={paymentVoucher}
                companyTaxes={companyTaxes}
                amountInWords={amountInWords}
                footer={footerTrue} />);
        }
        if (check === 2) {
            return (
                <CommercialInvoiceContentSecond commercialInvoiceNo={values.commercialInvoiceNo}
                    totalQty={totalQty}
                    amountData={amountData}
                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    amountInWords={amountInWords}
                    footer={footerTrue} />);
        }
        if (check === 3) {
            return (
                <CommercialInvoiceContentThird commercialInvoiceNo={values.commercialInvoiceNo}
                    totalQty={totalQty}
                    amountData={amountData}
                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    amountInWords={amountInWords}
                    footer={footerTrue} />);
        }
        if (check === 4) {
            return (
                <CommercialInvoiceContentFour commercialInvoiceNo={values.commercialInvoiceNo}
                    totalQty={totalQty}
                    amountData={amountData}
                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    amountInWords={amountInWords}
                    footer={footerTrue} />);
        }

        return <CommercialInvoiceContentFirst commercialInvoiceNo={values.commercialInvoiceNo}
            totalQty={totalQty}
            amountData={amountData}
            invoiceData={data}
            termConditions={invoiceTermConditions}
            companyBanks={companyBanks}
            shippingBooking={shippingBooking}
            shipments={shipments}
            paymentVoucher={paymentVoucher}
            companyTaxes={companyTaxes}
            amountInWords={amountInWords}
            footer={footerTrue} />;
    };




    return (
        <>
            <CustomPrintModel contentId="contentId">
                {getPrintContent()}
            </CustomPrintModel >

            <CustomPrintModel contentId="contentpdf">
                {getPdfContent()}
            </CustomPrintModel >

            <Box sx={{ mx: 'auto', mt: 0 }}>
                <Formik initialValues={values} enableReinitialize>
                    {() => (
                        <Form>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#212529" }}>
                                        {t("Commercial Invoice")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Box>
                                        <IconButton aria-label="delete">
                                            <DeleteOutlineOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={printContent}>
                                            <LocalPrintshopOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={pdfContent}>
                                            <PictureAsPdfOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="close" onClick={handleClose} >
                                            <CloseIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Divider />
                            <Grid container spacing={0} sx={{ px: "0%" }}>
                                <Grid item xs={12}>
                                    <CustomCardView title="Bill To" label={labels} data={values} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                            {t("Particulars")}
                                        </Typography>
                                    </Box>
                                    <DetailsParticulars
                                        title={'Particulars'}
                                        columns={particularsColumns}
                                        data={dataParticular}
                                        footerData={footerData}
                                    />
                                </Grid>
                                <Grid item xs={7}>
                                    <br />
                                    <Box>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("In Words")}
                                        </Typography>
                                        <Typography style={{ color: "#212529", fontWeight: 400, fontSize: "14px", textTransform: "uppercase" }}>
                                            {currencyName} {amountInWords}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ pt: 5 }}>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("Note")} :
                                        </Typography>
                                        <Typography style={{ color: "#212529", fontWeight: 400, fontSize: "14px", textTransform: "capitalize" }}>
                                            {values.note}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={5}>
                                    <br />
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                            {t("Summary")}
                                        </Typography>
                                    </Box>
                                    <Card sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 400 }} variant="outlined">
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography>{t("Total Quantity")}:</Typography>
                                            <Typography>{totalQty}</Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography>{t("Amount")}:</Typography>
                                            <Typography> {amountData}</Typography>
                                        </Box>

                                        <Divider sx={{ my: 0 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography fontWeight="bold">{t("Grand Total")}:</Typography>
                                            <Typography fontWeight="bold">{amountData}</Typography>
                                        </Box>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Box>



        </>
    );
};

export default CommercialInvoiceView;